/**
 * On-request floorplan digitization, editor side (spec 2026-09-03-01 §F1–§F3).
 *
 * Owns everything about one run: the enqueue, the poll, the summary and the
 * failure. It deliberately does not touch the document — the page reloads the
 * scene on success, exactly as the import does, because the job wrote a new
 * representation the editor has never seen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { $api } from '../../api'
import { useScopeLive } from '../../contexts/LiveEventsContext'
import { useElapsedSeconds, useJobPoll } from './useJobPoll'

const DIGITIZE_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/digitize' as const
const JOBS_PATH = '/api/v1/organizations/{orgCode}/jobs' as const

/** The digitize job's execution output, as far as the editor reads it. */
export interface DigitizeOutput {
    representation_uid?: string
    counts?: { walls?: number; spaces?: number; openings?: number; equipments?: number }
    matches?: { space_id: string; asset_uid: string; label: string }[]
    unmatched_labels?: string[]
    ambiguous_labels?: string[]
    /** §5a: was the result any good? Empty on a clean run. */
    validation?: {
        issues?: { code: string; severity: string; element_id?: string; message?: string }[]
        errors?: number
        warnings?: number
        valid_spaces?: number
    }
    /** `flatpx/1`, or `split/1` when a composite floor was redone per dwelling. */
    strategy?: string
    /**
     * Spec 2026-09-30-04 §5: validation found errors, so nothing was written.
     * The bundle waits here for the user to accept it.
     */
    preview?: DigitizePreview
}

/** A digitization left as a preview: the value it would have written. */
export interface DigitizePreview {
    value?: {
        spaces?: { id: string; name?: string; boundary?: { x: number; y: number }[] }[]
        walls?: { id: string; start?: { x: number; y: number }; end?: { x: number; y: number } }[]
    }
    broken_space_ids?: string[]
    accepted_at?: string
}

interface JobLike {
    uid: string
    final_status?: string | null
    executions?: { output?: unknown; error?: unknown; status?: string }[]
}

export interface DigitizeNotice {
    error: boolean
    message: string
    /** Set on a failure the user can retry with `replace` already ticked. */
    retryWithReplace?: boolean
    /** The API or job error code, when there is one (e.g. `CALIBRATION_LOW_CONFIDENCE`). */
    code?: string
    /**
     * Set on `FRAME_NOT_CALIBRATED` (spec 2026-09-05-07 §2). The editor's view
     * of the calibration is what let the button be clicked, so it is stale by
     * definition — the page reloads the scene, and the retry then lands on the
     * dialog's "calibrate first" branch instead of the same refusal.
     */
    staleCalibration?: boolean
}

export interface DigitizeState {
    /** False once the endpoint has answered 503 — the org has no model. */
    available: boolean
    running: boolean
    jobUid: string | null
    elapsedSeconds: number
    notice: DigitizeNotice | null
    enqueueing: boolean
}

export interface DigitizePlan extends DigitizeState {
    start: (opts: { representationUid?: string; replace: boolean }) => Promise<void>
    dismiss: () => void
    setNotice: (notice: DigitizeNotice | null) => void
}

/**
 * @param onFinished called once, after a successful run, with the job output
 * and the job's uid — the page reloads the scene there, or shows the preview.
 */
export function useDigitizePlan(
    org: string,
    assetUid: string,
    onFinished: (output: DigitizeOutput, jobUid: string) => void | Promise<void>,
): DigitizePlan {
    const queryClient = useQueryClient()
    const [available, setAvailable] = useState(() => !wasUnavailable())
    const [jobUid, setJobUid] = useState<string | null>(null)
    const [startedAt, setStartedAt] = useState<number | null>(null)
    const [elapsedSeconds, setElapsedSeconds] = useState(0)
    const [notice, setNotice] = useState<DigitizeNotice | null>(null)
    const settledRef = useRef<string | null>(null)

    const enqueue = $api.useMutation('post', DIGITIZE_PATH)

    // A run started before this mount — a reload, or a navigation away and back
    // — must be picked up, or the editor would offer to start a second one.
    // `status=running` means "no final status yet": queued or running.
    const inFlightQuery = $api.useQuery(
        'get',
        JOBS_PATH,
        {
            params: {
                query: {
                    type: 'floorplan_digitize',
                    target_entity_type: 'asset',
                    target_entity_uid: assetUid,
                    status: 'running',
                    limit: 1,
                },
                path: { orgCode: org },
            },
        } as never,
        { enabled: !!assetUid },
    )

    useEffect(() => {
        if (jobUid) return
        const jobs = (inFlightQuery.data as { jobs?: JobLike[] } | undefined)?.jobs ?? []
        const found = jobs[0]
        if (found?.uid && settledRef.current !== found.uid) {
            setJobUid(found.uid)
            setStartedAt(Date.now())
        }
    }, [inFlightQuery.data, jobUid])

    // §1: a fixed 10 s poll, never `useDynamicRefresh` — that hook pins itself
    // to the 5-minute live interval whenever the asset scope's socket is up,
    // which is exactly how a finished half-minute run went unnoticed.
    const poll = useJobPoll(org, jobUid)

    // A live event on the asset scope is welcome to bring the next check
    // forward. It is an *extra* refetch on top of the poll, never a
    // replacement: the poll is what guarantees the run is noticed at all.
    const live = useScopeLive({ entity: 'assets', uid: assetUid })
    const bump = poll.bump
    useEffect(() => {
        if (live) bump()
    }, [live, bump])

    // Elapsed time, for the progress strip. One second is plenty of precision
    // for a half-minute run.
    const elapsed = useElapsedSeconds(startedAt)
    useEffect(() => setElapsedSeconds(elapsed), [elapsed])

    const job = poll.job as JobLike | undefined
    const finalStatus = poll.finalStatus

    useEffect(() => {
        if (!jobUid || !finalStatus) return
        if (settledRef.current === jobUid) return
        settledRef.current = jobUid
        const execution = job?.executions?.[job.executions.length - 1]
        if (finalStatus === 'success') {
            void onFinished((execution?.output ?? {}) as DigitizeOutput, jobUid)
        } else {
            const message = errorMessageOf(execution?.error)
            setNotice({ error: true, message, code: jobErrorCode(message) })
        }
        setJobUid(null)
        setStartedAt(null)
        void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[1] === JOBS_PATH })
    }, [finalStatus, jobUid, job, onFinished, queryClient])

    const start = useCallback(async (opts: { representationUid?: string; replace: boolean }) => {
        setNotice(null)
        try {
            const res = await enqueue.mutateAsync({
                params: { path: { orgCode: org, assetUid } },
                body: {
                    ...(opts.representationUid ? { representation_uid: opts.representationUid } : {}),
                    ...(opts.replace ? { replace: true } : {}),
                },
            } as never) as { job_uid?: string }
            if (res?.job_uid) {
                settledRef.current = null
                setJobUid(res.job_uid)
                setStartedAt(Date.now())
            }
        } catch (err) {
            // openapi-react-query rejects with the parsed error *body*, not a
            // Response, so the code is what identifies the case — not a status.
            const code = codeOf(err)
            if (code === 'EXTRACTION_UNAVAILABLE') {
                setAvailable(false)
                rememberUnavailable()
            }
            setNotice({
                error: true,
                message: apiErrorMessage(err),
                code,
                retryWithReplace: code === 'EXTRACTION_EXISTS',
                staleCalibration: code === 'FRAME_NOT_CALIBRATED',
            })
        }
    }, [enqueue, org, assetUid])

    return useMemo(() => ({
        available,
        running: !!jobUid,
        jobUid,
        elapsedSeconds,
        notice,
        enqueueing: enqueue.isPending,
        start,
        dismiss: () => setNotice(null),
        setNotice,
    }), [available, jobUid, elapsedSeconds, notice, enqueue.isPending, start])
}

/**
 * The API's error body is flat — `{ title, code }` (see
 * `internal/handlers/base`). openapi-react-query hands it to the caller as the
 * rejection value, so these two read it rather than a Response.
 */
function codeOf(err: unknown): string | undefined {
    const body = err as { code?: string; error?: { code?: string } } | undefined
    return body?.code ?? body?.error?.code
}

export function apiErrorMessage(err: unknown): string {
    const body = err as { title?: string; message?: string; error?: { message?: string } } | undefined
    return body?.title ?? body?.message ?? body?.error?.message ?? String(err)
}

/**
 * "No vision model here" is a property of the organization, not of one click:
 * remembering it for the session keeps the button from reappearing on every
 * navigation back into the editor (spec 2026-09-03-01 §F1).
 */
const UNAVAILABLE_KEY = 'plan-digitize-unavailable'

function rememberUnavailable() {
    try {
        sessionStorage.setItem(UNAVAILABLE_KEY, '1')
    } catch {
        // A browser with storage disabled simply re-learns it on the next click.
    }
}

function wasUnavailable(): boolean {
    try {
        return sessionStorage.getItem(UNAVAILABLE_KEY) === '1'
    } catch {
        return false
    }
}

/** The `CODE:` prefix the job puts on its errors, e.g. `CALIBRATION_LOW_CONFIDENCE: …`. */
export function jobErrorCode(message: string): string | undefined {
    const m = /^([A-Z][A-Z_]+):/.exec(message)
    return m?.[1]
}

function errorMessageOf(raw: unknown): string {
    if (!raw) return 'unknown error'
    if (typeof raw === 'string') return raw
    const obj = raw as { message?: string; error?: string }
    return obj.message ?? obj.error ?? JSON.stringify(raw)
}
